/**
 * ForumFloatingPayBar — mobile-only sticky bottom bar.
 *
 * As of 31 May 2026 Forum: this bar now routes mobile users directly to the
 * live Zoom meeting (same as the rest of the page CTAs). The previous paid
 * checkout flow was retired in favour of the free live-link join experience
 * for opted-in attendees. Kept as a component (not removed) so it continues
 * to surface the live event prominently on small screens.
 */
import React, { useEffect, useState } from 'react';

const ZOOM_URL = 'https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1';

const STYLES = `
.ffpb-host{position:fixed;left:0;right:0;bottom:0;z-index:240;pointer-events:none;}
.ffpb-bar{
  pointer-events:auto;
  margin:0 12px 12px;
  background:#0D1B2A;
  border:1px solid rgba(200,168,75,.45);
  border-radius:14px;
  box-shadow:0 12px 40px rgba(13,27,42,.55), 0 2px 8px rgba(0,0,0,.18);
  transform:translateY(120%);
  transition:transform .42s cubic-bezier(.16,1,.3,1);
  overflow:hidden;
}
.ffpb-bar.visible{transform:translateY(0);}
.ffpb-row{display:flex;align-items:center;gap:12px;padding:11px 14px;}
.ffpb-icon{flex-shrink:0;width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#C8A84B 0%,#D9BC6A 100%);color:#0D1B2A;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;letter-spacing:.04em;}
.ffpb-text{flex:1;color:#FAF7F0;line-height:1.25;text-decoration:none;}
.ffpb-text-h{font-family:'Cormorant Display',Georgia,serif;font-size:15px;font-weight:700;letter-spacing:-.005em;}
.ffpb-text-h em{color:#C8A84B;font-style:italic;}
.ffpb-text-s{font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;color:rgba(250,247,240,.62);font-weight:600;margin-top:2px;}
.ffpb-cta{flex-shrink:0;background:#C8A84B;color:#0D1B2A;font-weight:800;letter-spacing:.08em;text-transform:uppercase;font-size:11px;padding:10px 14px;border-radius:8px;border:none;cursor:pointer;white-space:nowrap;text-decoration:none;}
.ffpb-close{flex-shrink:0;width:26px;height:26px;border-radius:50%;background:rgba(250,247,240,.08);color:rgba(250,247,240,.7);border:none;cursor:pointer;font-size:13px;line-height:1;display:flex;align-items:center;justify-content:center;margin-left:2px;}
@media (min-width:769px){.ffpb-host{display:none;}}
`;

const DISMISS_KEY = 'skia_ffpb_dismissed_v2';

export default function ForumFloatingPayBar() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      const threshold = window.innerHeight * 0.7;
      if (window.scrollY > threshold) setVisible(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  if (dismissed) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="ffpb-host" data-testid="forum-floating-pay-bar">
      <style>{STYLES}</style>
      <div className={`ffpb-bar ${visible ? 'visible' : ''}`}>
        <div className="ffpb-row">
          <div className="ffpb-icon" aria-hidden="true">▶</div>
          <a
            href={ZOOM_URL}
            target="_blank"
            rel="noreferrer"
            className="ffpb-text"
            data-testid="forum-floating-join-link"
          >
            <div className="ffpb-text-h"><em>Live Sunday</em> · 6:00 PM BST</div>
            <div className="ffpb-text-s">31 May · Invest UAE Forum</div>
          </a>
          <a
            href={ZOOM_URL}
            target="_blank"
            rel="noreferrer"
            className="ffpb-cta"
            data-testid="forum-floating-join-cta"
          >
            Join Zoom →
          </a>
          <button
            className="ffpb-close"
            aria-label="Dismiss"
            onClick={dismiss}
            data-testid="forum-floating-pay-close"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
